import {Inject, LOCALE_ID, Pipe, PipeTransform} from '@angular/core';
import {formatNumber} from '@angular/common';
import {DistanceUnit} from '../domain/utils/distanceUnit';
import {DISTANCE_UNIT} from '../tokens/distanceUnit.token';

/**
 * When providing a LOCALE_ID or DISTANCE_UNIT with an extended string that uses a service for toString(),
 * the impure pipe allows to evaluate again the toString() and have the newest value using the service.
 *
 * The service used should only return the current value of a behaviour subject to have good performance
 * and avoid performances problems with impure pipe.
 */
@Pipe({
    name: 'kilometerToDistance',
    pure: false
})
export class KilometerToDistanceImpurePipe implements PipeTransform {

    constructor(@Inject(LOCALE_ID) private locale: string,
        @Inject(DISTANCE_UNIT) private distanceUnit: string) {
    }

    /**
     *
     * @param valueInKilometers The number to be formatted
     * @param digitsInfo Decimal representation options, specified by a string in the following format:
     * <code>{minIntegerDigits}.{minFractionDigits}-{maxFractionDigits}</code>
     * <ul>
     * <li>minIntegerDigits: The minimum number of integer digits before the decimal point. Default is 1.</li>
     * <li>minFractionDigits: The minimum number of digits after the decimal point. Default is 0.</li>
     * <li>maxFractionDigits: The maximum number of digits after the decimal point. Default is 3.</li>
     * </ul>
     * From Angular DecimalPipe. Optional. Default is undefined.
     */
    transform(valueInKilometers: number, digitsInfo?: string): string {
        let distanceFormat: Array<string> = [];
        // Use .toString() since you can provide class extending String and that override toString()
        let isMeasurementInMiles: boolean = this.distanceUnit.toString() as DistanceUnit === DistanceUnit.MI;

        if (isMeasurementInMiles) {
            distanceFormat.push(formatNumber(valueInKilometers / 1.609344, this.locale, digitsInfo), ' mi');
        } else if (valueInKilometers >= 1) {
            distanceFormat.push(formatNumber(valueInKilometers, this.locale, digitsInfo), ' km');
        } else {
            distanceFormat.push(formatNumber(valueInKilometers * 1_000, this.locale, digitsInfo), ' m');
        }

        if (distanceFormat[0] === '0') {
            distanceFormat = ['<', isMeasurementInMiles ? '0.1' : '100', distanceFormat[1]];
        }

        return distanceFormat.join(' ');
    }
}

